/**
 * Iter 154 · Phase A — CRM → Finance Revenue Flow dashboard.
 *
 * Single page combining:
 *   • Funnel: Pipeline (weighted) → Quoted → SO → Invoiced (accrual) →
 *             Collected (cash) → Outstanding
 *   • Monthly stacked bars: Quoted vs Invoiced vs Collected
 *   • Aged Receivables buckets (current / 0-30 / 31-60 / 61-90 / 90+)
 *   • Pipeline-weighted Cash Flow forecast (weekly, next 90 days)
 *   • Multi-currency clearly labelled: everything reported in base currency
 *     (workspace `finance.base_currency` — default INR). Foreign-currency
 *     invoices/leads are FX-converted using the workspace `fx_rates`
 *     collection at document-date resolution.
 *
 * Route: /app/finance/revenue-flow
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Target, FileText, ShoppingCart, Receipt, IndianRupee, AlertTriangle, TrendingUp, TrendingDown, Clock, ArrowRight } from "lucide-react";
import { apiErrorMessage } from "@/lib/apiError";

function fmt(n, ccy = "INR") {
  const v = Number(n || 0);
  const sym = ccy === "INR" ? "₹" : ccy === "USD" ? "$" : ccy === "EUR" ? "€" : `${ccy} `;
  const abs = Math.abs(v);
  if (abs >= 1e7) return `${sym}${(v / 1e7).toFixed(2)}Cr`;
  if (abs >= 1e5) return `${sym}${(v / 1e5).toFixed(2)}L`;
  if (abs >= 1e3) return `${sym}${(v / 1e3).toFixed(1)}K`;
  return `${sym}${v.toFixed(0)}`;
}

export default function RevenueFlow() {
  const { activeWorkspaceId } = useAuth();
  const nav = useNavigate();
  const [months, setMonths] = useState(6);
  const [horizon, setHorizon] = useState(90);
  const [flow, setFlow] = useState(null);
  const [aged, setAged] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    if (!activeWorkspaceId) return;
    setErr(null);
    try {
      const [f, a, fc] = await Promise.all([
        api.get(`/workspaces/${activeWorkspaceId}/revenue-flow?months=${months}`),
        api.get(`/workspaces/${activeWorkspaceId}/aged-receivables`),
        api.get(`/workspaces/${activeWorkspaceId}/cashflow-forecast?horizon_days=${horizon}`),
      ]);
      setFlow(f.data); setAged(a.data); setForecast(fc.data);
    } catch (e) {
      setErr(apiErrorMessage(e, "Couldn't load revenue flow"));
    }
  }, [activeWorkspaceId, months, horizon]);
  useEffect(() => { load(); }, [load]);

  if (err) return <div className="p-6 text-sm text-destructive">{err}</div>;
  if (!flow || !aged || !forecast) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;

  const ccy = flow.funnel.currency;

  return (
    <>
      <PageHeader
        breadcrumb="Finance"
        title="Revenue Flow"
        description="CRM → Finance visibility · pipeline to cash · accrual and cash-basis side-by-side"
        testid="revenue-flow-header"
        actions={
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Window</span>
            <Select value={String(months)} onValueChange={(v) => setMonths(Number(v))}>
              <SelectTrigger className="h-8 rounded-none w-28 text-xs" data-testid="rf-months"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[3, 6, 12, 24].map((n) => <SelectItem key={n} value={String(n)}>{n} months</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        }
      />
      <div className="px-4 sm:px-6 md:px-10 py-4 space-y-6" data-testid="revenue-flow">
        <FunnelStrip funnel={flow.funnel} counts={flow.stage_counts} nav={nav} />

        <Tabs defaultValue="waterfall" className="w-full">
          <TabsList className="rounded-none">
            <TabsTrigger value="waterfall" className="rounded-none" data-testid="rf-tab-waterfall">Cash flow waterfall</TabsTrigger>
            <TabsTrigger value="monthly" className="rounded-none" data-testid="rf-tab-monthly">Monthly bars</TabsTrigger>
            <TabsTrigger value="aged" className="rounded-none" data-testid="rf-tab-aged">Aged receivables</TabsTrigger>
            <TabsTrigger value="forecast" className="rounded-none" data-testid="rf-tab-forecast">90-day forecast</TabsTrigger>
          </TabsList>

          <TabsContent value="waterfall" className="pt-4">
            <CashflowWaterfallPane wsId={activeWorkspaceId} />
          </TabsContent>

          <TabsContent value="monthly" className="pt-4">
            <MonthlyBars monthly={flow.monthly} currency={ccy} />
          </TabsContent>

          <TabsContent value="aged" className="pt-4">
            <AgedTable aged={aged} />
          </TabsContent>

          <TabsContent value="forecast" className="pt-4">
            <ForecastPane forecast={forecast} horizon={horizon} setHorizon={setHorizon} />
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
}

// ─────────── Funnel strip ───────────

function FunnelStrip({ funnel, counts, nav }) {
  const ccy = funnel.currency;
  const stages = [
    { key: "pipeline", label: "Pipeline (weighted)", icon: Target, value: funnel.pipeline_value, tone: "text-blue-600", tint: "bg-blue-50", sub: `${counts.leads_open} open leads`, href: "/app/leads" },
    { key: "quoted", label: "Quoted", icon: FileText, value: funnel.quoted_value, tone: "text-indigo-600", tint: "bg-indigo-50", sub: `${counts.quotations_sent} sent`, href: "/app/sales/quotations" },
    { key: "so", label: "Sales orders", icon: ShoppingCart, value: funnel.so_value, tone: "text-purple-600", tint: "bg-purple-50", sub: `${counts.so_open} open`, href: "/app/sales/orders" },
    { key: "invoiced", label: "Invoiced (accrual)", icon: Receipt, value: funnel.invoiced_value, tone: "text-amber-600", tint: "bg-amber-50", sub: `${counts.invoices_open} open · ${counts.invoices_paid} paid`, href: "/app/sales/invoices" },
    { key: "collected", label: "Collected (cash)", icon: IndianRupee, value: funnel.collected_value, tone: "text-emerald-600", tint: "bg-emerald-50", sub: "payments in window", href: "/app/sales/payments" },
    { key: "outstanding", label: "Outstanding AR", icon: AlertTriangle, value: funnel.outstanding_value, tone: "text-red-600", tint: "bg-red-50", sub: "balance_due sum", href: "/app/sales/invoices" },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="rf-funnel">
      {stages.map((s, i) => {
        const Icon = s.icon;
        return (
          <button key={s.key} type="button" onClick={() => nav(s.href)}
                  className={`border border-border p-3 text-left hover:ring-1 hover:ring-primary ${s.tint}`}
                  data-testid={`rf-stage-${s.key}`}>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground">
              <Icon className={`w-3 h-3 ${s.tone}`} /> {s.label}
              {i < stages.length - 1 && <ArrowRight className="w-3 h-3 ml-auto opacity-30" />}
            </div>
            <div className={`text-xl font-mono font-semibold mt-1 ${s.tone}`}>{fmt(s.value, ccy)}</div>
            <div className="text-[10px] text-muted-foreground mt-0.5 truncate">{s.sub}</div>
          </button>
        );
      })}
    </div>
  );
}

// ─────────── Monthly bars ───────────

function MonthlyBars({ monthly, currency }) {
  const max = useMemo(() => {
    return Math.max(1, ...monthly.flatMap((r) => [r.quoted, r.invoiced, r.collected]));
  }, [monthly]);
  return (
    <div className="border border-border" data-testid="rf-monthly">
      <div className="px-3 py-2 border-b border-border flex items-center gap-3 text-xs">
        <span className="font-semibold uppercase tracking-widest">Monthly flow</span>
        <span className="text-[10px] text-muted-foreground">Base currency: {currency}</span>
        <div className="ml-auto flex items-center gap-3 text-[10px] text-muted-foreground">
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-indigo-500" /> Quoted</span>
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-amber-500" /> Invoiced (accrual)</span>
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-emerald-500" /> Collected (cash)</span>
        </div>
      </div>
      <div className="p-3">
        <div className="flex items-end gap-3 h-64">
          {monthly.map((r) => (
            <div key={r.month} className="flex-1 flex flex-col items-center gap-1" data-testid={`rf-month-${r.month}`}>
              <div className="flex-1 w-full flex items-end gap-1">
                <div className="flex-1 bg-indigo-500/80 relative group" style={{ height: `${(r.quoted / max) * 100}%` }}
                     title={`Quoted: ${fmt(r.quoted, currency)}`} />
                <div className="flex-1 bg-amber-500/80 relative" style={{ height: `${(r.invoiced / max) * 100}%` }}
                     title={`Invoiced: ${fmt(r.invoiced, currency)}`} />
                <div className="flex-1 bg-emerald-500/80 relative" style={{ height: `${(r.collected / max) * 100}%` }}
                     title={`Collected: ${fmt(r.collected, currency)}`} />
              </div>
              <div className="text-[10px] font-mono text-muted-foreground">{r.month.slice(5)}</div>
            </div>
          ))}
        </div>
        <div className="mt-3 border-t border-border pt-2">
          <table className="w-full text-xs" data-testid="rf-monthly-table">
            <thead className="text-[10px] uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="text-left py-1">Month</th>
                <th className="text-right py-1">Quoted</th>
                <th className="text-right py-1">Invoiced <span className="text-[8px]">(accrual)</span></th>
                <th className="text-right py-1">Collected <span className="text-[8px]">(cash)</span></th>
                <th className="text-right py-1">AR gap</th>
              </tr>
            </thead>
            <tbody className="font-mono">
              {monthly.map((r) => (
                <tr key={r.month} className="border-t border-border">
                  <td className="py-1">{r.month}</td>
                  <td className="text-right text-indigo-600">{fmt(r.quoted, currency)}</td>
                  <td className="text-right text-amber-600">{fmt(r.invoiced, currency)}</td>
                  <td className="text-right text-emerald-600">{fmt(r.collected, currency)}</td>
                  <td className="text-right">{fmt(r.invoiced - r.collected, currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─────────── Aged AR ───────────

function AgedTable({ aged }) {
  const BUCKETS = [
    { key: "current", label: "Current", tone: "bg-emerald-500" },
    { key: "0_30", label: "0-30 days", tone: "bg-amber-400" },
    { key: "31_60", label: "31-60 days", tone: "bg-amber-600" },
    { key: "61_90", label: "61-90 days", tone: "bg-orange-600" },
    { key: "90_plus", label: "90+ days", tone: "bg-red-600" },
  ];
  const total = aged.total_outstanding || 0;
  const bmap = Object.fromEntries((aged.buckets || []).map((b) => [b.key, b.amount]));
  return (
    <div className="space-y-3" data-testid="rf-aged">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {BUCKETS.map((b) => (
          <div key={b.key} className="border border-border p-3" data-testid={`rf-aged-${b.key}`}>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{b.label}</div>
            <div className="text-xl font-mono font-semibold mt-1">{fmt(bmap[b.key] || 0, aged.currency)}</div>
            <div className="mt-2 h-1.5 bg-muted"><div className={`h-full ${b.tone}`} style={{ width: `${((bmap[b.key] || 0) / (total || 1)) * 100}%` }} /></div>
          </div>
        ))}
      </div>
      <div className="border border-border overflow-x-auto">
        <div className="px-3 py-2 border-b border-border bg-muted/40 text-xs font-semibold uppercase tracking-widest">
          Outstanding invoices · {aged.invoices.length} · {fmt(total, aged.currency)}
        </div>
        <table className="w-full text-xs" data-testid="rf-aged-table">
          <thead className="text-[10px] uppercase tracking-widest text-muted-foreground bg-muted/20">
            <tr>
              <th className="text-left px-2 py-1">Invoice</th>
              <th className="text-left px-2 py-1">Client</th>
              <th className="text-left px-2 py-1">Due date</th>
              <th className="text-right px-2 py-1">Days overdue</th>
              <th className="text-right px-2 py-1">Balance</th>
              <th className="text-right px-2 py-1">Balance (base)</th>
              <th className="text-left px-2 py-1">Bucket</th>
            </tr>
          </thead>
          <tbody className="font-mono">
            {aged.invoices.slice(0, 100).map((inv) => (
              <tr key={inv.id} className="border-t border-border" data-testid={`rf-aged-row-${inv.id}`}>
                <td className="px-2 py-1">{inv.invoice_no || inv.id.slice(0, 8)}</td>
                <td className="px-2 py-1 font-sans truncate max-w-[160px]">{inv.client_name}</td>
                <td className="px-2 py-1">{inv.due_date?.slice(0, 10) || "—"}</td>
                <td className={`px-2 py-1 text-right ${inv.days_overdue > 90 ? "text-red-600" : inv.days_overdue > 30 ? "text-amber-600" : "text-muted-foreground"}`}>
                  {inv.days_overdue > 0 ? inv.days_overdue : "—"}
                </td>
                <td className="px-2 py-1 text-right">{fmt(inv.balance_due, inv.currency)}</td>
                <td className="px-2 py-1 text-right">{fmt(inv.balance_due_base, aged.currency)}</td>
                <td className="px-2 py-1"><span className="chip text-[9px]">{inv.bucket}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-[10px] text-muted-foreground italic">
        Aging is based on days past <b>due_date</b>. Balance shown in each invoice&apos;s original
        currency <em>and</em> converted to base currency ({aged.currency}) at the invoice&apos;s
        issue-date FX rate.
      </div>
    </div>
  );
}

// ─────────── Cash-flow forecast ───────────

function ForecastPane({ forecast, horizon, setHorizon }) {
  const rows = forecast.weekly || [];
  const max = Math.max(1, ...rows.map((r) => Math.max(r.in, r.out)));
  const minCum = Math.min(0, ...rows.map((r) => r.cumulative));
  const maxCum = Math.max(1, ...rows.map((r) => r.cumulative));
  return (
    <div className="space-y-3" data-testid="rf-forecast">
      <div className="flex items-center gap-3 text-xs">
        <span className="font-semibold uppercase tracking-widest">Cash flow forecast</span>
        <span className="text-[10px] text-muted-foreground italic">Pipeline-weighted · base {forecast.summary.currency}</span>
        <Select value={String(horizon)} onValueChange={(v) => setHorizon(Number(v))}>
          <SelectTrigger className="h-7 rounded-none w-28 text-xs ml-auto" data-testid="rf-horizon"><SelectValue /></SelectTrigger>
          <SelectContent>{[30, 60, 90, 120, 180].map((h) => <SelectItem key={h} value={String(h)}>{h} days</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Stat label="Cash in" value={fmt(forecast.summary.total_in, forecast.summary.currency)} icon={TrendingUp} tone="text-emerald-600" testid="rf-forecast-in" />
        <Stat label="Cash out" value={fmt(forecast.summary.total_out, forecast.summary.currency)} icon={TrendingDown} tone="text-red-600" testid="rf-forecast-out" />
        <Stat label="Net" value={fmt(forecast.summary.net, forecast.summary.currency)} icon={IndianRupee} tone={forecast.summary.net >= 0 ? "text-emerald-600" : "text-red-600"} testid="rf-forecast-net" />
        <Stat label="Horizon" value={`${forecast.summary.horizon_days}d`} icon={Clock} tone="text-primary" testid="rf-forecast-horizon" />
      </div>

      <div className="border border-border p-3">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Weekly in/out (bars) & cumulative net (line)</div>
        <div className="flex items-end gap-1 h-40">
          {rows.map((r) => (
            <div key={r.week_start} className="flex-1 flex flex-col items-center gap-0.5 min-w-0" title={`Week ${r.week_start}: +${r.in} -${r.out} net ${r.net}`}>
              <div className="flex-1 w-full flex items-end gap-0.5">
                <div className="flex-1 bg-emerald-500/70" style={{ height: `${(r.in / max) * 100}%` }} />
                <div className="flex-1 bg-red-500/70" style={{ height: `${(r.out / max) * 100}%` }} />
              </div>
              <div className="w-full h-1">
                <div className={`h-full ${r.cumulative >= 0 ? "bg-primary" : "bg-red-600"}`}
                     style={{ width: `${((r.cumulative - minCum) / ((maxCum - minCum) || 1)) * 100}%` }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="border border-border overflow-x-auto">
        <table className="w-full text-xs" data-testid="rf-forecast-table">
          <thead className="text-[10px] uppercase tracking-widest text-muted-foreground bg-muted/20">
            <tr>
              <th className="text-left px-2 py-1">Week starting</th>
              <th className="text-right px-2 py-1 text-emerald-600">In</th>
              <th className="text-right px-2 py-1 text-red-600">Out</th>
              <th className="text-right px-2 py-1">Net</th>
              <th className="text-right px-2 py-1">Cumulative</th>
            </tr>
          </thead>
          <tbody className="font-mono">
            {rows.map((r) => (
              <tr key={r.week_start} className="border-t border-border" data-testid={`rf-forecast-week-${r.week_start}`}>
                <td className="px-2 py-1">{r.week_start}</td>
                <td className="px-2 py-1 text-right text-emerald-600">{fmt(r.in, forecast.summary.currency)}</td>
                <td className="px-2 py-1 text-right text-red-600">{fmt(r.out, forecast.summary.currency)}</td>
                <td className="px-2 py-1 text-right">{fmt(r.net, forecast.summary.currency)}</td>
                <td className={`px-2 py-1 text-right ${r.cumulative < 0 ? "text-red-600 font-bold" : ""}`}>{fmt(r.cumulative, forecast.summary.currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-[10px] text-muted-foreground italic">
        Forecast blends confirmed AR (invoice balance_due at due_date) with weighted pipeline
        (lead value × stage win-probability at expected close). This is a
        <b> projection</b>, not a GAAP-recognised figure — no journals are affected.
      </div>
    </div>
  );
}

function Stat({ label, value, icon: Icon, tone, testid }) {
  return (
    <div className="border border-border p-3" data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground">
        <Icon className={`w-3 h-3 ${tone}`} /> {label}
      </div>
      <div className={`text-xl font-mono font-semibold mt-1 ${tone}`}>{value}</div>
    </div>
  );
}

// ─────────── Cashflow Waterfall Pane ───────────

function CashflowWaterfallPane({ wsId }) {
  const params = new URLSearchParams(window.location.search);
  const initialDrill = params.get("drill");
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 90);
    return d.toISOString().slice(0, 10);
  });
  const [toDate, setToDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 90);
    return d.toISOString().slice(0, 10);
  });
  const [data, setData] = useState(null);
  const [risk, setRisk] = useState(null);
  const [loading, setLoading] = useState(false);
  const [drill, setDrill] = useState(null); // {segment_key, ...data}
  const [drillLoading, setDrillLoading] = useState(false);

  const load = useCallback(async () => {
    if (!wsId) return;
    setLoading(true);
    try {
      const [r, ar] = await Promise.all([
        api.get(`/workspaces/${wsId}/cashflow-waterfall?from_date=${fromDate}&to_date=${toDate}`),
        api.get(`/workspaces/${wsId}/sales-cc/ar-at-risk`).catch(() => ({ data: null })),
      ]);
      setData(r.data);
      setRisk(ar.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [wsId, fromDate, toDate]);

  useEffect(() => { load(); }, [load]);

  // Iter 159 — auto-open drill from ?drill=segment_key.
  useEffect(() => {
    if (initialDrill && data && !drill) {
      const seg = data.segments.find((s) => s.key === initialDrill);
      if (seg) openDrill(initialDrill, seg.label);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialDrill, data]);

  const openDrill = async (segmentKey, label) => {
    if (segmentKey === "opening" || segmentKey === "closing") return;
    setDrillLoading(true);
    setDrill({ segment_key: segmentKey, label, count: 0, total: 0, rows: [] });
    try {
      const r = await api.get(`/workspaces/${wsId}/cashflow-waterfall/segment/${segmentKey}?from_date=${fromDate}&to_date=${toDate}&limit=100`);
      setDrill({ segment_key: segmentKey, label, ...r.data });
    } catch { /* ignore */ }
    finally { setDrillLoading(false); }
  };

  if (!data) return <div className="text-sm text-muted-foreground">{loading ? "Loading waterfall…" : "No data"}</div>;
  const ccy = data.currency || "INR";

  let cum = data.opening_cash;
  const walk = [{ key: "opening", label: "Opening cash", amount: data.opening_cash, cum: cum, kind: "start" }];
  data.segments.forEach((s) => {
    if (s.kind === "informational") return;
    cum = s.kind === "in" ? cum + s.amount : cum - s.amount;
    walk.push({ ...s, cum });
  });
  walk.push({ key: "closing", label: "Closing cash (cash-basis)", amount: data.closing_cash, cum: data.closing_cash, kind: "end" });

  const maxCum = Math.max(...walk.map((w) => Math.abs(w.cum)), 1);

  return (
    <div className="space-y-4" data-testid="rf-waterfall">
      <div className="flex flex-wrap items-center gap-3 rounded-none border border-border bg-muted/30 p-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">From</div>
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
                 className="h-8 rounded-none border border-border bg-background px-2 text-xs"
                 data-testid="waterfall-from" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">To</div>
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
                 className="h-8 rounded-none border border-border bg-background px-2 text-xs"
                 data-testid="waterfall-to" />
        </div>
        <Button size="sm" variant="outline" className="rounded-none ml-auto" onClick={load} disabled={loading} data-testid="waterfall-refresh">
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <KpiTile label="Opening cash" value={fmt(data.opening_cash, ccy)} tint="bg-slate-50" tone="text-slate-800" testid="waterfall-opening" />
        <KpiTile label="Closing cash (cash-basis)" value={fmt(data.closing_cash, ccy)} tint="bg-emerald-50" tone="text-emerald-700" testid="waterfall-closing" />
        <KpiTile label="Projected closing (incl. pipeline+SO+invoiced)" value={fmt(data.projected_closing_cash, ccy)} tint="bg-blue-50" tone="text-blue-700" testid="waterfall-projected" />
        <KpiTile label="Bills open (informational drag)" value={fmt(data.segments.find((s) => s.key === "bills_open")?.amount || 0, ccy)} tint="bg-amber-50" tone="text-amber-700" testid="waterfall-bills-open" />
      </div>

      {/* AR at Risk callout — who to chase this week */}
      {risk && risk.total_at_risk > 0 && (
        <div className="rounded-none border-l-4 border-red-500 bg-red-50 p-4" data-testid="waterfall-ar-risk">
          <div className="flex items-start gap-3">
            <div className="flex-1">
              <div className="text-xs uppercase tracking-widest text-red-700 font-bold">⚠ AR at Risk · Who to chase this week</div>
              <div className="mt-1 text-2xl font-mono font-bold text-red-700">
                {fmt(risk.total_at_risk, ccy)} <span className="text-sm text-red-600 font-sans font-normal">across {risk.invoice_count} invoices</span>
              </div>
              <div className="mt-3 grid grid-cols-1 md:grid-cols-5 gap-2">
                {(risk.top_risks || []).slice(0, 5).map((r) => (
                  <div key={r.invoice_id} className="rounded-none bg-white border border-red-200 p-2" data-testid={`waterfall-risk-${r.invoice_id}`}>
                    <div className="text-xs font-semibold truncate">{r.client_name}</div>
                    <div className="text-[10px] text-muted-foreground">
                      {r.doc_no} · <span className={r.days_overdue > 60 ? "text-red-600 font-bold" : "text-amber-700"}>{r.days_overdue}d overdue</span>
                    </div>
                    <div className="mt-1 font-mono text-sm font-bold text-red-600">{fmt(r.risk_amount, ccy)}</div>
                    {r.client_avg_days_to_pay != null && (
                      <div className="text-[10px] text-muted-foreground">avg pays in {r.client_avg_days_to_pay}d</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="rounded-none border border-border bg-white">
        <div className="border-b border-border px-3 py-2 label-eyebrow text-[10px]">Cash flow walk — {ccy}</div>
        <div className="p-4 space-y-2">
          {walk.map((w) => {
            const isInflow = w.kind === "in";
            const isOutflow = w.kind === "out";
            const width = Math.min(100, Math.max(4, (Math.abs(w.cum) / maxCum) * 100));
            const color = w.kind === "start" ? "bg-slate-400"
              : w.kind === "end" ? "bg-emerald-600"
              : isInflow ? "bg-blue-500"
              : isOutflow ? "bg-red-500"
              : "bg-gray-400";
            const clickable = w.kind === "in" || w.kind === "out";
            return (
              <button
                key={w.key}
                type="button"
                onClick={() => clickable && openDrill(w.key, w.label)}
                disabled={!clickable}
                className={`w-full flex items-center gap-3 ${clickable ? "hover:bg-muted/30 cursor-pointer" : "cursor-default"} rounded transition-colors`}
                data-testid={`waterfall-row-${w.key}`}
              >
                <div className="w-64 shrink-0 text-xs text-left py-1">
                  <div className="font-semibold flex items-center gap-1">
                    {w.label}
                    {clickable && <ArrowRight className="h-3 w-3 opacity-40" />}
                  </div>
                  {w.detail?.count != null && (
                    <div className="text-[10px] text-muted-foreground">{w.detail.count} record(s){w.detail?.raw_total ? ` · raw ${fmt(w.detail.raw_total, ccy)}` : ""}</div>
                  )}
                </div>
                <div className="flex-1 h-6 relative bg-muted/30">
                  <div className={`h-6 ${color} transition-all`} style={{ width: `${width}%` }} />
                  <div className="absolute inset-0 flex items-center px-2 text-[11px] font-mono font-semibold">
                    {isInflow ? "+" : isOutflow ? "−" : ""}{fmt(Math.abs(isInflow || isOutflow ? w.amount : w.cum), ccy)}
                  </div>
                </div>
                <div className="w-28 shrink-0 text-right text-xs font-mono">
                  = {fmt(w.cum, ccy)}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="rounded-none border border-border bg-muted/20 p-3 text-xs text-muted-foreground">
        <b>How to read this:</b> Blue bars = cash inflows, red bars = cash outflows.
        &nbsp;<b>Closing cash</b> uses ONLY cash-basis actuals (Opening + Collected − Bills paid) — the reliable number for what you actually have.
        &nbsp;<b>Projected closing</b> layers on pipeline (weighted quotations), committed SOs, and invoiced (accrual) — the optimistic view if everything you&apos;ve promised comes through.
        &nbsp;<b>Bills open</b> is shown separately as an accrued drag not yet paid.
        &nbsp;<b>Tip:</b> click any bar to see the underlying documents.
      </div>

      {/* Drill-down slide-over */}
      {drill && (
        <div className="fixed inset-0 z-50 flex" onClick={() => setDrill(null)} data-testid="waterfall-drill">
          <div className="flex-1 bg-black/40" />
          <div className="w-full max-w-2xl bg-white h-full overflow-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-border p-4 flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground">Drill · {drill.segment_key}</div>
                <div className="text-lg font-bold">{drill.label}</div>
                <div className="text-xs text-muted-foreground">{drill.count} record(s) · Total {fmt(drill.total, ccy)}</div>
              </div>
              <button onClick={() => setDrill(null)} className="rounded-none border border-border px-3 py-1 text-xs hover:bg-muted" data-testid="drill-close">Close</button>
            </div>
            {drillLoading ? (
              <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-[10px] uppercase tracking-widest text-muted-foreground">
                  <tr>
                    <th className="text-left p-3">Doc #</th>
                    <th className="text-left p-3">Client / Ref</th>
                    <th className="text-left p-3">Date</th>
                    <th className="text-right p-3">Amount</th>
                    <th className="text-right p-3">Extra</th>
                  </tr>
                </thead>
                <tbody>
                  {(drill.rows || []).map((r) => (
                    <tr key={r.id} className="border-t border-border hover:bg-muted/20"
                        data-testid={`drill-row-${r.id}`}>
                      <td className="p-3 font-mono text-xs">{r.doc_no}</td>
                      <td className="p-3">{r.client_name}</td>
                      <td className="p-3 text-xs text-muted-foreground">{r.date}</td>
                      <td className="p-3 text-right font-mono font-semibold">{fmt(r.amount, ccy)}</td>
                      <td className="p-3 text-right text-xs text-muted-foreground">
                        {r.weighted != null ? `weighted ${fmt(r.weighted, ccy)}` : ""}
                        {r.balance_due != null ? `bal ${fmt(r.balance_due, ccy)}` : ""}
                        {r.status ? ` · ${r.status}` : ""}
                        {r.method ? ` · ${r.method}` : ""}
                      </td>
                    </tr>
                  ))}
                  {(!drill.rows || drill.rows.length === 0) && (
                    <tr><td colSpan={5} className="p-8 text-center text-muted-foreground text-sm">No records in window</td></tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function KpiTile({ label, value, tint, tone, testid }) {
  return (
    <div className={`rounded-none border border-border p-3 ${tint}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`text-2xl font-mono font-semibold ${tone}`}>{value}</div>
    </div>
  );
}

